import { Injectable, computed, effect, inject, signal, type Signal } from '@angular/core';

import { ThemeService } from '../theme/theme.service.js';
import { ES_BRANDING } from './branding.token.js';
import type { BrandingConfig } from './branding.types.js';

/**
 * Holds the active branding and keeps the DOM in sync:
 *
 * - Writes brand colors to CSS variables on `<html>` (`--brand-primary`,
 *   `--brand-accent`, `--brand-font`) so generic Tailwind classes
 *   (`bg-brand-primary`) resolve to the customer's palette.
 * - Sets `document.title` to the brand name.
 * - Swaps `logoUrl` ↔ `logoDarkUrl` based on the active theme.
 *
 * Consumers pull `name()` / `logoUrl()` as signals — templates re-render
 * automatically when the brand is swapped at runtime (e.g. tenant switch).
 */
@Injectable({ providedIn: 'root' })
export class BrandingService {
  private readonly _base = signal<BrandingConfig>(inject(ES_BRANDING));
  private readonly _config = signal<BrandingConfig>(this._base());
  private readonly theme = inject(ThemeService);

  /** Full active branding config — either the base or a contextual override. */
  readonly config: Signal<BrandingConfig> = this._config.asReadonly();

  /** Brand name — use wherever `'EchoSpaces'` used to be hardcoded. */
  readonly name = computed(() => this._config().name);

  /**
   * Theme-aware logo URL: `logoDarkUrl` when the resolved theme is dark
   * and a dark variant was provided, otherwise `logoUrl`.
   */
  readonly logoUrl = computed(() => {
    const cfg = this._config();
    return this.theme.isDark() && cfg.logoDarkUrl ? cfg.logoDarkUrl : cfg.logoUrl;
  });

  constructor() {
    effect(() => {
      const cfg = this._config();
      this.applyCssVariables(cfg);
      this.applyDocumentTitle(cfg);
    });
  }

  /**
   * Replace the base branding at runtime (e.g. a full tenant switch). Any
   * contextual override is discarded. Consumers should prefer this over
   * `applyOverride` when the change should survive route navigation.
   */
  apply(config: BrandingConfig): void {
    this._base.set(config);
    this._config.set(config);
  }

  /**
   * Temporarily merge a partial brand on top of the base — used by pages
   * that render inside an organization's context (e.g. an island detail
   * page applies the owning org's palette). Fields omitted fall back to
   * the base. Call `reset()` on leave.
   */
  applyOverride(partial: Partial<BrandingConfig>): void {
    this._config.set({ ...this._base(), ...partial });
  }

  /** Restore the base branding after an `applyOverride`. */
  reset(): void {
    this._config.set(this._base());
  }

  private applyCssVariables(cfg: BrandingConfig): void {
    if (typeof document === 'undefined') return;
    const root = document.documentElement;
    root.style.setProperty('--brand-primary', cfg.primaryColor);
    root.style.setProperty('--brand-accent', cfg.accentColor);
    root.style.setProperty('--brand-font', cfg.fontFamily);
  }

  private applyDocumentTitle(cfg: BrandingConfig): void {
    if (typeof document === 'undefined') return;
    document.title = cfg.name;
  }
}
